import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../shared/components';
import { api } from '../../utils/api';
import './Vocabulary.css';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'known', label: 'Known' },
  { key: 'review', label: 'Still learning' },
  { key: 'unseen', label: 'New' },
];

export default function WordBrowser({ onExit }) {
  const [words, setWords] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/api/vocab/list')
      .then((data) => {
        setWords(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Unable to load the word bank right now.');
        setLoading(false);
      });
  }, []);

  async function markStatus(id, status) {
    setWords((prev) => prev.map((w) => (w.id === id ? { ...w, status } : w)));
    try {
      await api.post(`/api/vocab/${id}/review`, { status });
    } catch (err) {
      setError('Unable to save your progress right now.');
    }
  }

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return words.filter((w) => {
      if (filter !== 'all' && w.status !== filter) return false;
      if (!q) return true;
      return w.word.toLowerCase().includes(q) || w.definition.toLowerCase().includes(q);
    });
  }, [words, search, filter]);

  const knownCount = words.filter((w) => w.status === 'known').length;

  return (
    <div className="vocabulary-page">
      <button className="vocabulary-browse-link" onClick={onExit}>&larr; Back to today&rsquo;s word</button>
      <h1>Word Bank</h1>
      <p className="vocabulary-intro">
        {loading ? 'Loading…' : `${knownCount} of ${words.length} words marked known.`}
      </p>
      {error && <p className="vocabulary-error">{error}</p>}

      <div className="vocabulary-browser-controls">
        <input
          className="vocabulary-search"
          type="text"
          placeholder="Search words or definitions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="vocabulary-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`vocabulary-filter ${filter === f.key ? 'vocabulary-filter--active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {!loading && visible.length === 0 && <p className="vocabulary-empty">No words match.</p>}

      <div className="vocabulary-list">
        {visible.map((w) => (
          <Card key={w.id} className="vocabulary-list-item">
            <div className="vocabulary-list-item-header">
              <span className="vocabulary-list-word">{w.word}</span>
              <span className="vocabulary-list-pos">{w.part_of_speech}</span>
              <span className={`vocabulary-badge vocabulary-badge--${w.status}`}>
                {w.status === 'known' ? 'Known' : w.status === 'review' ? 'Still learning' : 'New'}
              </span>
            </div>
            <p className="vocabulary-list-definition">{w.definition}</p>
            <p className="vocabulary-list-example">&ldquo;{w.example}&rdquo;</p>
            <div className="vocabulary-list-actions">
              <button className="vocabulary-list-action" onClick={() => markStatus(w.id, 'review')}>Still learning</button>
              <button className="vocabulary-list-action vocabulary-list-action--primary" onClick={() => markStatus(w.id, 'known')}>I know this</button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
